import type { Hyperliquid } from '../../index'
import type {
  ClearinghouseState,
  FundingHistory,
  Meta,
  MetaAndAssetCtxs,
  PerpsAtOpenInterestCap,
  PredictedFundings,
  UserFunding,
  UserNonFundingLedgerUpdates,
} from '../../types'
import { InfoType } from '../../types/constants'
import type { HttpApi } from '../../utils/helpers'
import type { SymbolConversion } from '../../utils/symbolConversion'

export class PerpetualsInfoAPI {
  private httpApi: HttpApi
  private symbolConversion: SymbolConversion
  private parent: Hyperliquid

  constructor(httpApi: HttpApi, symbolConversion: SymbolConversion, parent: Hyperliquid) {
    this.httpApi = httpApi
    this.symbolConversion = symbolConversion
    this.parent = parent
  }

  async getMeta(rawResponse = false): Promise<Meta> {
    const response = await this.httpApi.makeRequest({ type: InfoType.META })
    return rawResponse
      ? response
      : await this.symbolConversion.convertResponse(response, ['name', 'coin', 'symbol'], 'PERP')
  }

  async getMetaAndAssetCtxs(rawResponse = false): Promise<MetaAndAssetCtxs> {
    const response = await this.httpApi.makeRequest({ type: InfoType.PERPS_META_AND_ASSET_CTXS })
    return rawResponse
      ? response
      : await this.symbolConversion.convertResponse(response, ['name', 'coin', 'symbol'], 'PERP')
  }

  async getClearinghouseState(
    user: string,
    rawResponse = false,
  ): Promise<ClearinghouseState> {
    const response = await this.httpApi.makeRequest({
      type: InfoType.PERPS_CLEARINGHOUSE_STATE,
      user,
    })
    return rawResponse ? response : await this.symbolConversion.convertResponse(response)
  }

  async getUserFunding(
    user: string,
    startTime: number,
    endTime?: number,
    rawResponse = false,
  ): Promise<UserFunding> {
    const response = await this.httpApi.makeRequest(
      {
        type: InfoType.USER_FUNDING,
        user,
        startTime,
        endTime,
      },
      20,
    )
    return rawResponse ? response : await this.symbolConversion.convertResponse(response)
  }

  async getUserNonFundingLedgerUpdates(
    user: string,
    startTime: number,
    endTime?: number,
    rawResponse = false,
  ): Promise<UserNonFundingLedgerUpdates> {
    const response = await this.httpApi.makeRequest(
      {
        type: InfoType.USER_NON_FUNDING_LEDGER_UPDATES,
        user,
        startTime,
        endTime,
      },
      20,
    )
    return rawResponse ? response : await this.symbolConversion.convertResponse(response)
  }

  async getFundingHistory(
    coin: string,
    startTime: number,
    endTime?: number,
    rawResponse = false,
  ): Promise<FundingHistory> {
    await this.parent.ensureInitialized()
    const response = await this.httpApi.makeRequest(
      {
        type: InfoType.FUNDING_HISTORY,
        coin: await this.symbolConversion.convertSymbol(coin, 'reverse'),
        startTime,
        endTime,
      },
      20,
    )
    return rawResponse ? response : await this.symbolConversion.convertResponse(response)
  }

  async getPredictedFundings(rawResponse = false): Promise<PredictedFundings> {
    const response = await this.httpApi.makeRequest(
      {
        type: InfoType.PREDICTED_FUNDINGS,
      },
      20,
    )

    return rawResponse ? response : await this.symbolConversion.convertResponse(response)
  }

  async getPerpsAtOpenInterestCap(rawResponse = false): Promise<PerpsAtOpenInterestCap> {
    const response = (await this.httpApi.makeRequest({
      type: InfoType.PERPS_AT_OPEN_INTEREST_CAP,
    })) as string[]

    if (rawResponse) {
      return response
    }

    // Convert each symbol in the array
    const convertedResponse = await Promise.all(
      response.map((symbol: string) => this.symbolConversion.convertSymbol(symbol, '', 'PERP')),
    )

    return convertedResponse
  }
}
